import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX, ChevronLeft, ChevronRight, Building2 } from 'lucide-react';
import api, { getFullImageUrl } from '../../services/api';
import './HomePromo.css';

/**
 * 首页企业宣传区块
 * - 从 /api/promo 获取配置（后台可编辑）
 * - mediaType=video：播放器 + 控制条
 * - mediaType=images：轮播图
 */
const HomePromo = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const videoRef = useRef(null);
  const carouselTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/promo');
        if (cancelled) return;
        if (res.data?.success && res.data.data) {
          setConfig(res.data.data);
          if (typeof res.data.data.muted === 'boolean') setMuted(res.data.data.muted);
        } else {
          setError(true);
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const images = Array.isArray(config?.images) ? config.images.filter(Boolean) : [];
  const useImages = config?.mediaType === 'images' && images.length > 0;

  useEffect(() => {
    if (!useImages || images.length < 2) return;
    carouselTimerRef.current = setInterval(() => {
      setCarouselIdx((i) => (i + 1) % images.length);
    }, 4500);
    return () => clearInterval(carouselTimerRef.current);
  }, [useImages, images.length]);

  if (loading) return null;
  if (error || !config || config.enabled === false) return null;

  const usingVideo = !useImages && !!config.videoUrl;

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play(); setPlaying(true);
    } else {
      v.pause(); setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const gotoIdx = (i) => {
    if (!images.length) return;
    setCarouselIdx((i + images.length) % images.length);
  };

  return (
    <section className="home__section home-promo-section home-fade-in home-fade-in--delay-2">
      <div className="home__section-head">
        <div className="home__section-head-main">
          <span className="home__section-code">PROMO</span>
          <div>
            <h2>{config.title || 'Inside the Desk'}</h2>
            <p className="home__section-desc">{config.subtitle || ''}</p>
          </div>
        </div>
        <span className="home__section-meta">
          <Building2 size={13} aria-hidden="true" style={{ marginRight: 6, verticalAlign: '-2px' }} />
          {config.eyebrow || 'INSIDE THE DESK'}
        </span>
      </div>

      <div className={`home-promo ${usingVideo ? 'home-promo--video' : 'home-promo--images'}`}>
        {/* 装饰性玻璃边框 */}
        <div className="home-promo__frame" aria-hidden="true">
          <span className="home-promo__corner home-promo__corner--tl" />
          <span className="home-promo__corner home-promo__corner--tr" />
          <span className="home-promo__corner home-promo__corner--bl" />
          <span className="home-promo__corner home-promo__corner--br" />
        </div>

        {usingVideo && (
          <div className="home-promo__player">
            <video
              ref={videoRef}
              className="home-promo__video"
              src={getFullImageUrl(config.videoUrl)}
              poster={config.posterUrl ? getFullImageUrl(config.posterUrl) : undefined}
              autoPlay={config.autoplay !== false}
              muted={muted}
              loop={config.loop !== false}
              playsInline
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
            />
            <div className="home-promo__controls">
              <button
                type="button"
                className="home-promo__ctrl-btn"
                onClick={togglePlay}
                aria-label={playing ? 'Pause' : 'Play'}
              >
                {playing ? <Pause size={16} /> : <Play size={16} />}
              </button>
              <button
                type="button"
                className="home-promo__ctrl-btn"
                onClick={toggleMute}
                aria-label={muted ? 'Unmute' : 'Mute'}
              >
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <span className="home-promo__badge">LIVE B-ROLL</span>
            </div>
          </div>
        )}

        {useImages && (
          <div className="home-promo__carousel">
            {images.map((url, i) => (
              <div
                key={url + i}
                className={`home-promo__slide ${i === carouselIdx ? 'is-active' : ''}`}
                style={{ backgroundImage: `url(${getFullImageUrl(url)})` }}
                aria-hidden={i !== carouselIdx}
              />
            ))}

            {images.length > 1 && (
              <>
                <button
                  type="button"
                  className="home-promo__nav home-promo__nav--prev"
                  onClick={() => gotoIdx(carouselIdx - 1)}
                  aria-label="Previous"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  type="button"
                  className="home-promo__nav home-promo__nav--next"
                  onClick={() => gotoIdx(carouselIdx + 1)}
                  aria-label="Next"
                >
                  <ChevronRight size={18} />
                </button>

                <div className="home-promo__dots">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`home-promo__dot ${i === carouselIdx ? 'is-active' : ''}`}
                      onClick={() => gotoIdx(i)}
                      aria-label={`Slide ${i + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {!usingVideo && !useImages && (
          <div className="home-promo__empty">No promo media yet. Upload a video or images in the admin panel.</div>
        )}
      </div>
    </section>
  );
};

export default HomePromo;
